import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { Sparkles, ArrowLeft, Upload, Download, AlertCircle, CheckCircle } from 'lucide-react';

const BulkUpload = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get('/products/bulk-upload/template', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product_upload_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      
      if (!validTypes.includes(fileExtension)) {
        toast.error('Please upload a CSV or Excel file');
        return;
      }
      
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post('/products/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setResult(response.data);
      
      if (response.data.errors_count > 0) {
        toast.warning(`Uploaded ${response.data.products_created} products with ${response.data.errors_count} errors`);
      } else {
        toast.success(`Successfully uploaded ${response.data.products_created} products!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Navbar */}
      <nav className="border-b border-[#E5E1DC] bg-white/90 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <Sparkles className="h-7 w-7 text-[#1A3A52]" strokeWidth={1.5} />
            <span className="text-2xl font-serif font-semibold text-[#2C3E50]">
              LuxLyx
            </span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/brand/dashboard')}
          className="mb-8 text-[#5A6C7D] hover:text-[#1A3A52] hover:bg-[#F5F3F0]"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <Upload className="h-12 w-12 text-[#1A3A52] mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="text-4xl font-serif font-semibold mb-2 text-[#2C3E50]">Bulk Upload Products</h1>
            <p className="text-[#5A6C7D]">Upload multiple products at once using CSV or Excel</p>
          </div>

          <div className="bg-white border border-[#E5E1DC] rounded-2xl p-8 shadow-sm">
            {/* Instructions */}
            <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-lg mb-3 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-yellow-400" />
                Instructions
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Download the CSV template below</li>
                <li>Fill in your product information</li>
                <li>Upload the completed file</li>
                <li>Review the results and fix any errors</li>
              </ol>
            </div>

            {/* Download Template */}
            <div className="mb-8">
              <Button
                data-testid="download-template-btn"
                onClick={handleDownloadTemplate}
                variant="outline"
                className="w-full border-yellow-400 text-yellow-400 hover:bg-yellow-400/10"
              >
                <Download className="mr-2 h-5 w-5" />
                Download CSV Template
              </Button>
            </div>

            {/* File Upload */}
            <div className="mb-6">
              <Label htmlFor="file" className="text-gray-300 mb-2 block">
                Select CSV or Excel File
              </Label>
              <input
                id="file"
                data-testid="file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="w-full bg-black/40 border border-white/10 text-white rounded-lg p-3 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-yellow-400 file:text-black hover:file:bg-yellow-500"
              />
              {file && (
                <div className="mt-2 text-sm text-green-400 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Selected: {file.name}
                </div>
              )}
            </div>

            {/* Upload Button */}
            <Button
              data-testid="upload-btn"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold text-lg py-6 hover:opacity-90"
            >
              {uploading ? 'Uploading...' : 'Upload Products'}
            </Button>

            {/* Results */}
            {result && (
              <div className="mt-8 space-y-4">
                <div className="bg-green-400/10 border border-green-400/30 rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-2 flex items-center text-green-400">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Upload Summary
                  </h3>
                  <div className="space-y-2 text-gray-300">
                    <p>Products Created: <span className="font-bold text-green-400">{result.products_created}</span></p>
                    <p>Errors: <span className="font-bold text-red-400">{result.errors_count}</span></p>
                  </div>
                </div>

                {result.errors && result.errors.length > 0 && (
                  <div className="bg-red-400/10 border border-red-400/30 rounded-xl p-6">
                    <h3 className="font-bold text-lg mb-3 text-red-400">Errors Found</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {result.errors.map((error, index) => (
                        <div key={index} className="bg-black/40 rounded p-3 text-sm">
                          <p className="text-gray-300">
                            <span className="font-semibold text-red-400">Row {error.row}</span> - SKU: {error.sku}
                          </p>
                          <p className="text-gray-400">{error.error}</p>
                        </div>
                      ))}
                    </div>
                    {result.errors_count > result.errors.length && (
                      <p className="text-sm text-gray-400 mt-2">
                        + {result.errors_count - result.errors.length} more errors...
                      </p>
                    )}
                  </div>
                )}

                <Button
                  data-testid="view-products-btn"
                  onClick={() => navigate('/brand/dashboard')}
                  className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold"
                >
                  View Products in Dashboard
                </Button>
              </div>
            )}
          </div>

          {/* CSV Format Guide */}
          <div className="mt-8 bg-gradient-to-br from-[#1a1a1c] to-[#0f0f10] border border-white/10 rounded-2xl p-6">
            <h3 className="font-bold text-lg mb-4">CSV Column Guide</h3>
            <div className="space-y-3 text-sm">
              <div>
                <span className="font-semibold text-yellow-400">name</span>
                <span className="text-gray-400"> - Product name (required)</span>
              </div>
              <div>
                <span className="font-semibold text-yellow-400">description</span>
                <span className="text-gray-400"> - Product description (required)</span>
              </div>
              <div>
                <span className="font-semibold text-yellow-400">mrp</span>
                <span className="text-gray-400"> - Original retail price (required)</span>
              </div>
              <div>
                <span className="font-semibold text-yellow-400">current_price</span>
                <span className="text-gray-400"> - Sale price (required)</span>
              </div>
              <div>
                <span className="font-semibold text-yellow-400">stock</span>
                <span className="text-gray-400"> - Available quantity (required)</span>
              </div>
              <div>
                <span className="font-semibold text-yellow-400">sku</span>
                <span className="text-gray-400"> - Stock keeping unit (required)</span>
              </div>
              <div>
                <span className="font-semibold text-yellow-400">category</span>
                <span className="text-gray-400"> - Product category (required)</span>
              </div>
              <div>
                <span className="font-semibold text-yellow-400">images</span>
                <span className="text-gray-400"> - Comma-separated image URLs (optional)</span>
              </div>
              <div>
                <span className="font-semibold text-yellow-400">flash_sale_end</span>
                <span className="text-gray-400"> - ISO datetime (optional)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUpload;
